import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import type { CampaignDto, WhatsAppMarketingTemplateDto } from '@ecom/contracts';
import { Link } from 'react-router-dom';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useCampaignsStore, type CampaignReach } from '@/stores/campaigns.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date, dateTime, money, share } from '@/lib/format';
import { fromLocalInput, toLocalInput } from '@/lib/date-input';
import {
  AB_TEST_SHARES,
  AB_TEST_WAITS,
  campaignAudience,
  campaignDraft,
  campaignRequest,
  type CampaignChannel,
  type CampaignDraft,
  type CampaignField,
  type DraftError,
} from '@/lib/marketing';
import { MailIcon, PlusIcon } from '@/components/icons';
import { BilingualField, TagsField, ToggleField } from '@/components/fields';
import { AsyncSelect } from '@/components/async-select';
import { StagesField } from '@/components/customer-stage';
import { categoryOptions, productOptions, customerGroupOptions } from '@/lib/options';
import { useCustomersStore } from '@/stores/customers.store';
import { Menu, type MenuItem } from '@/components/menu';
import { ListCard, LoadMore, Page, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
  Switch,
  TableSkeleton,
  cx,
  type PillTone,
} from '@/components/ui';
import { useDraftError } from './marketing-shared';

const STATUS_TONES: Record<CampaignDto['status'], PillTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  TESTING: 'info',
  SENDING: 'warning',
  SENT: 'success',
  CANCELLED: 'danger',
};

/** How often a campaign being sent is re-read, so its counts move while the page is open. */
const PROGRESS_EVERY_MS = 5000;

type Confirming =
  | { kind: 'send'; campaign: CampaignDto; reach: CampaignReach | null; at?: string | null }
  | { kind: 'stop'; campaign: CampaignDto }
  | { kind: 'delete'; campaign: CampaignDto };

/**
 * Broadcasts (ADR-036).
 *
 * The screen is arranged around the one number that stops a mistake while it
 * is still stoppable — *how many people will this reach* — which is why the
 * count is asked for as the audience changes, and asked again, fresh, in the
 * confirmation before anything goes out. A draft opens in a panel from the far
 * edge, where it can be changed and tried on the owner's own inbox first.
 * Owner-only, like the API: nothing is asked for until the page knows it may.
 */
export function CampaignsPage() {
  const {
    items,
    nextCursor,
    loading,
    loadingMore,
    acting,
    error,
    load,
    loadMore,
    refreshSending,
    estimate,
    send,
    unschedule,
    pickWinner,
    cancel,
    remove,
  } = useCampaignsStore();
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [counting, setCounting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A send chosen for later (ADR-099): the time is asked first, the count after.
  const [scheduling, setScheduling] = useState<CampaignDto | null>(null);

  useEffect(() => {
    if (isOwner) void load();
  }, [isOwner, load]);

  const anySending = items.some((campaign) => campaign.status === 'SENDING' || (campaign.status === 'TESTING' && !campaign.abTest?.testEndsAt));
  useEffect(() => {
    if (!anySending) return undefined;
    const timer = setInterval(() => void refreshSending(), PROGRESS_EVERY_MS);
    return () => clearInterval(timer);
  }, [anySending, refreshSending]);

  if (!isOwner) {
    return (
      <Page title={t('campaigns')} fill>
        <ListCard>
          <EmptyState title={t('ownerOnlySettings')} icon={<MailIcon />} />
        </ListCard>
      </Page>
    );
  }

  /** The count is taken now, not when the draft was written: the customer list has moved since. */
  async function askToSend(campaign: CampaignDto, at: string | null = null) {
    setNotice(null);
    setCounting(campaign.id);
    const reach = await estimate(campaign.channel, campaign.audience);
    setCounting(null);
    setConfirming({ kind: 'send', campaign, reach, at });
  }

  async function confirm() {
    if (!confirming) return;
    const { kind, campaign } = confirming;

    const at = confirming.kind === 'send' ? (confirming.at ?? undefined) : undefined;
    const ok =
      kind === 'send' ? await send(campaign.id, at) : kind === 'stop' ? await cancel(campaign.id) : await remove(campaign.id);
    setConfirming(null);
    if (ok) setNotice(t(kind === 'send' ? (at ? 'campaignScheduledNotice' : 'campaignSending') : kind === 'stop' ? 'campaignStopped' : 'campaignDeleted'));
  }

  const newCampaign = (
    <Button
      onClick={() => {
        setNotice(null);
        setEditing('new');
      }}
    >
      <PlusIcon className="size-4" />
      {t('newCampaign')}
    </Button>
  );

  return (
    <Page title={t('campaigns')} subtitle={t('campaignsSubtitle')} actions={newCampaign} fill>
      <ListCard>
        {error || notice ? (
          <div className="flex flex-col gap-2 px-4 pt-4">
            {error ? <Alert>{error}</Alert> : null}
            {notice ? <Alert tone="success">{notice}</Alert> : null}
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noCampaigns')} body={t('noCampaignsHint')} icon={<MailIcon />} action={newCampaign} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('campaignName')}</Th>
                  <Th>{t('channel')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('campaignReached')}</Th>
                  <Th>{t('campaignWhen')}</Th>
                  <Th align="end">
                    <span className="sr-only">{t('moreActions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((campaign) => {
                  const isDraft = campaign.status === 'DRAFT';
                  const more: MenuItem[] = [
                    ...(isDraft ? [{ label: t('campaignEdit'), onSelect: () => setEditing(campaign.id) }] : []),
                    ...(isDraft ? [{ label: t('campaignSchedule'), onSelect: () => setScheduling(campaign) }] : []),
                    ...(campaign.status !== 'SENDING'
                      ? [
                          {
                            label: t('deleteEntry'),
                            danger: true,
                            onSelect: () => setConfirming({ kind: 'delete', campaign }),
                          },
                        ]
                      : []),
                  ];

                  return (
                    // A draft opens to be changed; what went out is a record, and stays as it is.
                    <Tr key={campaign.id} onClick={isDraft ? () => setEditing(campaign.id) : undefined}>
                      <Td>
                        <span className="block font-medium text-ink">{campaign.name}</span>
                        {campaign.subject ? (
                          <span className="block max-w-md truncate text-xs text-ink-muted">
                            {campaign.subject[locale] || campaign.subject.ar || campaign.subject.en}
                          </span>
                        ) : null}
                      </Td>
                      <Td className="text-ink-muted">
                        {campaign.channel === 'EMAIL' ? t('email') : campaign.channel === 'WHATSAPP' ? t('whatsapp') : t('sms')}
                      </Td>
                      <Td>
                        <Pill tone={STATUS_TONES[campaign.status]}>
                          {t(`campaignStatus${campaign.status}` as 'campaignStatusDRAFT')}
                        </Pill>
                      </Td>
                      <Td data-numeric>
                        {isDraft ? (
                          <span className="text-ink-faint">{t('dash')}</span>
                        ) : (
                          <>
                            <span className="block text-ink">
                              {t('campaignSentOf', {
                                sent: count(campaign.sentCount, locale),
                                total: count(campaign.recipientCount, locale),
                              })}
                            </span>
                            {campaign.failedCount > 0 ? (
                              <span className="block text-xs text-danger" title={t('campaignFailuresHint')}>
                                {t('campaignFailedCount', { count: count(campaign.failedCount, locale) })}
                              </span>
                            ) : null}
                            {campaign.channel === 'WHATSAPP' && campaign.sentCount > 0 ? (
                              // Meta's receipts (ADR-106): reached the phone, then read. WhatsApp reports no clicks.
                              <span className="block text-xs text-ink-muted">
                                {t('campaignWhatsAppResults', {
                                  delivered: share(campaign.deliveredCount, campaign.sentCount, locale),
                                  read: share(campaign.openedCount, campaign.sentCount, locale),
                                })}
                              </span>
                            ) : null}
                            {campaign.channel === 'EMAIL' && campaign.sentCount > 0 ? (
                              // What it did (ADR-099): opens are approximate, clicks and orders are not.
                              <span className="block text-xs text-ink-muted" title={t('campaignOpensHint')}>
                                {t('campaignResults', {
                                  opened: share(campaign.openedCount, campaign.sentCount, locale),
                                  clicked: share(campaign.clickedCount, campaign.sentCount, locale),
                                  orders: count(campaign.orders, locale),
                                  revenue: money(campaign.revenue, locale),
                                })}
                              </span>
                            ) : null}
                            {campaign.abTest ? <AbTestResults campaign={campaign} /> : null}
                          </>
                        )}
                      </Td>
                      <Td className="whitespace-nowrap text-ink-muted">
                        {campaign.status === 'SCHEDULED' && campaign.scheduledAt
                          ? t('campaignScheduledFor', { when: dateTime(campaign.scheduledAt, locale) })
                          : campaign.startedAt
                            ? dateTime(campaign.startedAt, locale)
                            : date(campaign.createdAt, locale)}
                      </Td>
                      <Td align="end">
                        <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                          {isDraft ? (
                            <Button size="sm" disabled={acting || counting !== null} onClick={() => void askToSend(campaign)}>
                              {counting === campaign.id ? t('campaignCounting') : t('sendCampaign')}
                            </Button>
                          ) : null}
                          {campaign.status === 'SCHEDULED' ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={acting}
                              onClick={() => void unschedule(campaign.id).then((ok) => ok && setNotice(t('campaignUnscheduledNotice')))}
                            >
                              {t('campaignUnschedule')}
                            </Button>
                          ) : null}
                          {campaign.status === 'TESTING' && campaign.abTest?.testEndsAt
                            ? (['A', 'B'] as const).map((variant) => (
                                <Button
                                  key={variant}
                                  variant="secondary"
                                  size="sm"
                                  disabled={acting}
                                  onClick={() =>
                                    void pickWinner(campaign.id, variant).then((ok) => ok && setNotice(t('abTestPicked', { variant })))
                                  }
                                >
                                  {t('abTestSendNow', { variant })}
                                </Button>
                              ))
                            : null}
                          {campaign.status === 'SENDING' || campaign.status === 'TESTING' ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={acting}
                              onClick={() => setConfirming({ kind: 'stop', campaign })}
                            >
                              {t('stop')}
                            </Button>
                          ) : null}
                          {more.length > 0 ? <Menu label={t('moreActions')} items={more} /> : null}
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? (
        <CampaignEditor
          key={editing}
          id={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setNotice(message);
          }}
        />
      ) : null}

      {scheduling ? (
        <ScheduleSheet
          campaign={scheduling}
          onClose={() => setScheduling(null)}
          onPick={(at) => {
            const campaign = scheduling;
            setScheduling(null);
            void askToSend(campaign, at);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming?.kind === 'send'
            ? t('campaignSendTitle')
            : confirming?.kind === 'stop'
              ? t('campaignStopTitle')
              : t('campaignDeleteTitle')
        }
        body={confirmBody(confirming, locale, t)}
        confirmLabel={
          confirming?.kind === 'send' ? t('sendCampaign') : confirming?.kind === 'stop' ? t('stop') : t('deleteEntry')
        }
        cancelLabel={t('cancel')}
        tone={confirming?.kind === 'send' ? 'primary' : 'danger'}
        busy={acting}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void confirm()}
      />
    </Page>
  );
}

/** When a campaign goes (ADR-099): a time in the operator's own clock, at least a few minutes ahead. */
function ScheduleSheet({ campaign, onClose, onPick }: { campaign: CampaignDto; onClose: () => void; onPick: (at: string) => void }) {
  const t = useT();
  const soonest = toLocalInput(new Date(Date.now() + 10 * 60_000).toISOString());
  const [when, setWhen] = useState(toLocalInput(new Date(Date.now() + 24 * 3_600_000).toISOString()));
  const at = fromLocalInput(when);
  const valid = at !== null && Date.parse(at) > Date.now() + 5 * 60_000;

  return (
    <Sheet
      open
      title={t('campaignSchedule')}
      subtitle={campaign.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button disabled={!valid} onClick={() => at && onPick(at)}>
            {t('campaignScheduleNext')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('campaignScheduleWhen')} htmlFor="campaign-when" hint={t('campaignScheduleHint')}>
          <Input id="campaign-when" type="datetime-local" dir="ltr" min={soonest} value={when} onChange={(event) => setWhen(event.target.value)} />
        </Field>
      </div>
    </Sheet>
  );
}

/** What happens, said before it does. */
function confirmBody(confirming: Confirming | null, locale: 'ar' | 'en', t: ReturnType<typeof useT>): string {
  if (!confirming) return '';
  const name = confirming.campaign.name;

  if (confirming.kind === 'send') {
    if (!confirming.reach) return t('campaignSendUnknown', { name });
    if (confirming.reach.count === 0) return t('campaignSendNobody', { name });
    // A scheduled send counts its audience again when it goes (ADR-099): the number is today's.
    return confirming.at
      ? t('campaignScheduleCount', { name, count: count(confirming.reach.count, locale), when: dateTime(confirming.at, locale) })
      : t('campaignSendCount', { name, count: count(confirming.reach.count, locale) });
  }
  if (confirming.kind === 'stop') return t('campaignStopBody');
  return confirming.campaign.status === 'DRAFT' ? t('campaignDeleteDraftBody') : t('campaignDeleteSentBody');
}

/**
 * The editor: a new draft, or a saved one fetched fresh — a draft somebody
 * sent from another tab is no longer one to change.
 */
function CampaignEditor({
  id,
  onClose,
  onSaved,
}: {
  id: string | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { current, currentLoading, saveError, open, clearSaveError } = useCampaignsStore();
  const t = useT();

  useEffect(() => {
    if (id) void open(id);
    else clearSaveError();
  }, [id, open, clearSaveError]);

  if (id && (currentLoading || current?.id !== id)) {
    return (
      <Sheet open size="lg" title={t('campaignEdit')} onClose={onClose}>
        {saveError ? (
          <Alert>{saveError}</Alert>
        ) : (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-24" />
            <Skeleton className="h-40" />
          </div>
        )}
      </Sheet>
    );
  }

  return <CampaignForm campaign={id ? current : null} onClose={onClose} onSaved={onSaved} />;
}

function CampaignForm({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: CampaignDto | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { saving, testing, saveError, create, update, sendTest, estimate } = useCampaignsStore();
  const ownEmail = useAuthStore((state) => state.user?.email ?? '');
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const errorText = useDraftError();
  const formId = useId();

  const initial = useMemo(() => campaignDraft(campaign), [campaign]);
  // The tags already in use, so the audience keeps to the store's own spellings (ADR-064).
  const { tagsInUse, loadTags } = useCustomersStore();
  useEffect(() => {
    void loadTags();
  }, [loadTags]);
  const [draft, setDraft] = useState<CampaignDraft>(initial);
  const [errors, setErrors] = useState<Partial<Record<CampaignField, DraftError>>>({});
  const [testTo, setTestTo] = useState(() => (campaign?.channel === 'SMS' ? '' : ownEmail));
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const editable = !campaign || campaign.status === 'DRAFT';

  const set = (patch: Partial<CampaignDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as CampaignField[]) delete next[key];
      return next;
    });
  };

  /*
   * Asked as the audience changes — the number is only useful while the send
   * can still be reconsidered — but only once typing pauses, so a city typed
   * letter by letter is one question, not seven.
   */
  const audienceKey = JSON.stringify([draft.channel, campaignAudience(draft)]);
  const [reach, setReach] = useState<{ key: string; value: CampaignReach | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const [channel, audience] = JSON.parse(audienceKey) as [CampaignChannel, ReturnType<typeof campaignAudience>];
    const timer = setTimeout(() => {
      void estimate(channel, audience).then((value) => {
        if (!cancelled) setReach({ key: audienceKey, value });
      });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [audienceKey, estimate]);

  const countingReach = reach?.key !== audienceKey;

  /** Saves what is on screen; on an existing draft, only when something changed. */
  async function save(): Promise<boolean> {
    const result = campaignRequest(draft);
    if (!result.ok) {
      setErrors(result.errors);
      return false;
    }

    if (!campaign) return create(result.body);
    if (JSON.stringify(draft) === JSON.stringify(initial)) return true;

    const { name, subject, body, audience, abTest } = result.body;
    // A test switched off is said as null, so the API takes it off (ADR-113).
    return update(campaign.id, { name, ...(subject ? { subject } : {}), body, audience, ...(draft.channel === 'EMAIL' ? { abTest: abTest ?? null } : {}) });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setTestResult(null);
    if (await save()) onSaved(t(campaign ? 'campaignSaved' : 'campaignDrafted'));
  }

  /** The test is of what is on screen, so unsaved changes are saved first. */
  async function handleTest() {
    if (!campaign) return;
    setTestResult(null);
    if (!(await save())) return;

    const to = testTo.trim();
    const sent = await sendTest(campaign.id, to);
    if (sent !== null) {
      setTestResult(sent ? { ok: true, text: t('campaignTestSent', { to }) } : { ok: false, text: t('campaignTestFailed') });
    }
  }

  const busy = saving || testing;

  return (
    <Sheet
      open
      size="lg"
      title={campaign ? t('campaignEdit') : t('newCampaign')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </Button>
          {editable ? (
            <Button type="submit" form={formId} disabled={busy}>
              {saving ? t('saving') : campaign ? t('save') : t('saveDraft')}
            </Button>
          ) : null}
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {!editable ? <Alert tone="info">{t('campaignNotDraft')}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('campaignName')} htmlFor="campaign-name" hint={t('campaignNameHint')} error={errorText(errors.name)}>
            <Input
              id="campaign-name"
              maxLength={120}
              value={draft.name}
              invalid={Boolean(errors.name)}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Field>

          <Field
            label={t('channel')}
            htmlFor="campaign-channel"
            hint={campaign ? t('campaignChannelFixed') : undefined}
          >
            {/* Fixed once saved: the API changes a draft's words and audience, not how it travels. */}
            <Select
              id="campaign-channel"
              value={draft.channel}
              disabled={Boolean(campaign)}
              onChange={(event) => {
                const channel = event.target.value as CampaignChannel;
                set({ channel });
                setTestTo(channel === 'EMAIL' ? ownEmail : '');
              }}
              className="w-full disabled:opacity-60"
            >
              <option value="EMAIL">{t('email')}</option>
              <option value="SMS">{t('sms')}</option>
              <option value="WHATSAPP">{t('whatsapp')}</option>
            </Select>
          </Field>
        </div>

        {draft.channel === 'EMAIL' ? (
          <div className="flex flex-col gap-1.5">
            <BilingualField label={t('subject')} value={draft.subject} onChange={(subject) => set({ subject })} />
            {errors.subject ? (
              <p role="alert" className="text-xs text-danger">
                {errorText(errors.subject)}
              </p>
            ) : null}
          </div>
        ) : null}

        {draft.channel === 'EMAIL' ? (
          // A second subject, tried on a slice first; the one with more clicks goes to the rest (ADR-113).
          <fieldset className="rounded-card border border-line p-4">
            <legend className="px-1 text-sm font-medium text-ink">{t('abTestTitle')}</legend>
            <label className="flex items-start gap-3">
              <Switch checked={draft.abTest} label={t('abTestTitle')} disabled={!editable} onChange={(abTest) => set({ abTest })} />
              <span className="text-xs text-ink-muted">{t('abTestHint')}</span>
            </label>
            {draft.abTest ? (
              <div className="mt-4 flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <BilingualField label={t('abTestSubjectB')} value={draft.subjectB} onChange={(subjectB) => set({ subjectB })} />
                  {errors.subjectB ? (
                    <p role="alert" className="text-xs text-danger">
                      {errorText(errors.subjectB)}
                    </p>
                  ) : null}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('abTestShare')} htmlFor="ab-share" hint={t('abTestShareHint', { each: String(draft.testPercent / 2) })}>
                    <Select
                      id="ab-share"
                      value={String(draft.testPercent)}
                      disabled={!editable}
                      onChange={(event) => set({ testPercent: Number(event.target.value) })}
                      className="w-full"
                    >
                      {AB_TEST_SHARES.map((share) => (
                        <option key={share} value={share}>
                          {`${share}%`}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t('abTestWait')} htmlFor="ab-wait" hint={t('abTestWaitHint')}>
                    <Select
                      id="ab-wait"
                      value={String(draft.testHours)}
                      disabled={!editable}
                      onChange={(event) => set({ testHours: Number(event.target.value) })}
                      className="w-full"
                    >
                      {AB_TEST_WAITS.map((hours) => (
                        <option key={hours} value={hours}>
                          {t('abTestHours', { hours: String(hours) })}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </div>
            ) : null}
          </fieldset>
        ) : null}

        {draft.channel === 'WHATSAPP' ? (
          <WhatsAppTemplatePicker
            value={draft.whatsappTemplate}
            onChange={(whatsappTemplate) => set({ whatsappTemplate })}
            error={errors.whatsappTemplate ? (errorText(errors.whatsappTemplate) ?? null) : null}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <BilingualField
              label={t('messageBody')}
              hint={t('campaignBodyHint')}
              value={draft.body}
              onChange={(body) => set({ body })}
              multiline
              rows={6}
              limit={5000}
            />
            {errors.body ? (
              <p role="alert" className="text-xs text-danger">
                {errorText(errors.body)}
              </p>
            ) : null}
          </div>
        )}

        <fieldset className="rounded-card border border-line p-4">
          <legend className="px-1 text-sm font-medium text-ink">{t('audience')}</legend>
          <p className="mb-4 text-xs text-ink-muted">{draft.channel === 'WHATSAPP' ? t('campaignWhatsAppAudience') : t('audienceConsentHint')}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('city')} htmlFor="campaign-city" hint={t('campaignCityHint')}>
              <Input id="campaign-city" value={draft.city} onChange={(event) => set({ city: event.target.value })} />
            </Field>
            <Field
              label={t('campaignJoinedSince')}
              htmlFor="campaign-joined"
              hint={t('optional')}
              error={errorText(errors.registeredAfter)}
            >
              <Input
                id="campaign-joined"
                type="date"
                dir="ltr"
                value={draft.registeredAfter}
                invalid={Boolean(errors.registeredAfter)}
                onChange={(event) => set({ registeredAfter: event.target.value })}
              />
            </Field>
            <Field
              label={t('campaignMinOrders')}
              htmlFor="campaign-min-orders"
              hint={t('campaignMinOrdersHint')}
              error={errorText(errors.minOrders)}
            >
              <Input
                id="campaign-min-orders"
                dir="ltr"
                inputMode="numeric"
                value={draft.minOrders}
                invalid={Boolean(errors.minOrders)}
                onChange={(event) => set({ minOrders: event.target.value })}
              />
            </Field>
            <Field
              label={t('campaignMaxOrders')}
              htmlFor="campaign-max-orders"
              hint={t('campaignMaxOrdersHint')}
              error={errorText(errors.maxOrders)}
            >
              <Input
                id="campaign-max-orders"
                dir="ltr"
                inputMode="numeric"
                value={draft.maxOrders}
                invalid={Boolean(errors.maxOrders)}
                onChange={(event) => set({ maxOrders: event.target.value })}
              />
            </Field>
            {/* What shoppers hold and what they bought (ADR-065). */}
            <Field label={t('campaignBoughtProduct')} htmlFor="campaign-bought-product" hint={t('optional')}>
              <AsyncSelect
                id="campaign-bought-product"
                value={draft.boughtProductId}
                emptyLabel={t('anyValue')}
                fetchPage={(search, cursor) => productOptions.page(search, cursor)}
                fetchOne={(id) => productOptions.one(id)}
                onChange={(id) => set({ boughtProductId: id })}
              />
            </Field>
            <Field label={t('campaignBoughtCategory')} htmlFor="campaign-bought-category" hint={t('campaignBoughtCategoryHint')}>
              <AsyncSelect
                id="campaign-bought-category"
                value={draft.boughtCategoryId}
                emptyLabel={t('anyValue')}
                fetchPage={(search, cursor) => categoryOptions.page(search, cursor)}
                fetchOne={(id) => categoryOptions.one(id)}
                onChange={(id) => set({ boughtCategoryId: id })}
              />
            </Field>
            <div className="sm:col-span-2">
              <StagesField
                id="campaign-stages"
                label={t('ruleStagesLabel')}
                hint={t('campaignStagesHint')}
                value={draft.stages}
                onChange={(stages) => set({ stages })}
              />
            </div>
            <Field label={t('customerGroup')} htmlFor="campaign-group" hint={t('campaignGroupHint')}>
              <AsyncSelect
                id="campaign-group"
                value={draft.groupId}
                emptyLabel={t('anyValue')}
                fetchPage={(search, cursor) => customerGroupOptions.page(search, cursor)}
                fetchOne={(id) => customerGroupOptions.one(id)}
                onChange={(id) => set({ groupId: id })}
              />
            </Field>
            <div className="sm:col-span-2">
              <TagsField
                id="campaign-tags"
                label={t('customerTags')}
                hint={t('campaignTagsHint')}
                value={draft.tags}
                onChange={(tags) => set({ tags })}
                suggestions={tagsInUse.map((entry) => entry.tag)}
              />
            </div>
            <ToggleField
              label={t('neverOrdered')}
              checked={draft.hasNeverOrdered}
              onChange={(hasNeverOrdered) => set({ hasNeverOrdered })}
            />
            <ToggleField
              label={t('campaignAbandonedCart')}
              hint={t('campaignAbandonedCartHint')}
              checked={draft.abandonedCart}
              onChange={(abandonedCart) => set({ abandonedCart })}
            />
            <ToggleField
              label={t('campaignPriceDrop')}
              hint={t('campaignPriceDropHint')}
              checked={draft.wishlistPriceDrop}
              onChange={(wishlistPriceDrop) => set({ wishlistPriceDrop })}
            />
          </div>

          <p className="mt-4 text-sm text-ink-muted" aria-live="polite">
            {t('willReach')}:{' '}
            <strong data-numeric className="text-ink">
              {countingReach ? t('campaignCounting') : reach?.value ? count(reach.value.count, locale) : t('dash')}
            </strong>
            {!countingReach && reach?.value ? (
              <span data-numeric className="ms-2 text-xs">
                {t('reachSplit', {
                  accounts: count(reach.value.accounts, locale),
                  subscribers: count(reach.value.subscribers, locale),
                })}
              </span>
            ) : null}
          </p>
        </fieldset>

        {campaign && editable ? (
          <section className="flex flex-col gap-2 rounded-card border border-line p-4">
            <h3 className="text-sm font-medium text-ink">{t('campaignTestTitle')}</h3>
            <label htmlFor="campaign-test-to" className="text-xs text-ink-muted">
              {t('campaignTestTo')}
            </label>
            <div className="flex flex-wrap gap-2">
              <Input
                id="campaign-test-to"
                dir="ltr"
                type={draft.channel === 'EMAIL' ? 'email' : 'tel'}
                autoComplete={draft.channel === 'EMAIL' ? 'email' : 'tel'}
                value={testTo}
                onChange={(event) => setTestTo(event.target.value)}
                className="min-w-0 flex-1"
              />
              <Button variant="secondary" disabled={busy || !testTo.trim()} onClick={() => void handleTest()}>
                {testing ? t('loading') : t('sendTest')}
              </Button>
            </div>
            <p className="text-xs text-ink-muted">
              {draft.channel === 'EMAIL' ? t('campaignTestEmailHint') : draft.channel === 'WHATSAPP' ? t('campaignTestWhatsAppHint') : t('campaignTestSmsHint')}
            </p>
            {testResult ? <Alert tone={testResult.ok ? 'success' : 'error'}>{testResult.text}</Alert> : null}
          </section>
        ) : !campaign ? (
          <p className="text-xs text-ink-muted">{t('campaignTestNeedsDraft')}</p>
        ) : null}

        {Object.keys(errors).length > 0 ? <Alert>{t('mktCheckFields')}</Alert> : null}
        {/* A refused draft stays open beside its reason — nothing typed is lost. */}
        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>
    </Sheet>
  );
}

/**
 * The store's own approved WhatsApp marketing templates (ADR-106), read from
 * Meta: a campaign sends one of them as it is — only `{{1}}`, the customer's
 * first name, is filled. Those the platform cannot send say why.
 */
function WhatsAppTemplatePicker({
  value,
  onChange,
  error,
}: {
  value: { name: string; language: string } | null;
  onChange: (template: { name: string; language: string }) => void;
  error: string | null;
}) {
  const t = useT();
  const [templates, setTemplates] = useState<WhatsAppMarketingTemplateDto[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    withSession(() => adminApi.whatsapp.marketingTemplates())
      .then((page) => setTemplates(page.items))
      .catch((caught: unknown) =>
        // Not connected is the store's next step, said in the reader's language; anything else is Meta's or the API's words.
        setFailure(
          (caught as { code?: string }).code === 'WHATSAPP_NOT_CONNECTED'
            ? t('whatsappNotConnected')
            : errorMessage(caught, 'Could not read your WhatsApp templates.'),
        ),
      );
  }, [t]);

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-ink">{t('campaignWhatsAppTemplate')}</legend>
      <p className="text-xs text-ink-muted">{t('campaignWhatsAppTemplateHint')}</p>
      {failure ? (
        <Alert>
          {failure}{' '}
          <Link to="/settings/whatsapp" className="font-medium underline">
            {t('whatsapp')}
          </Link>
        </Alert>
      ) : templates === null ? (
        <Skeleton className="h-24" />
      ) : templates.length === 0 ? (
        <Alert tone="info">{t('campaignWhatsAppNone')}</Alert>
      ) : (
        <div className="flex flex-col gap-2">
          {templates.map((template) => {
            const chosen = value?.name === template.name && value.language === template.language;
            return (
              <label
                key={`${template.name}/${template.language}`}
                className={cx(
                  'flex items-start gap-3 rounded-control border px-4 py-3',
                  chosen ? 'border-brand-600 bg-brand-50' : 'border-line',
                  template.usable ? 'cursor-pointer' : 'opacity-60',
                )}
              >
                <input
                  type="radio"
                  name="whatsapp-template"
                  className="mt-1 accent-brand-600"
                  disabled={!template.usable}
                  checked={chosen}
                  onChange={() => onChange({ name: template.name, language: template.language })}
                />
                <span className="min-w-0 flex-1">
                  <span dir="ltr" className="block text-start font-mono text-xs text-ink-muted">
                    {template.name} · {template.language}
                  </span>
                  {template.header && !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.header) ? (
                    <span dir="auto" className="mt-1 block text-sm font-semibold text-ink">
                      {template.header}
                    </span>
                  ) : null}
                  <span dir="auto" className="mt-1 block whitespace-pre-line text-sm text-ink">
                    {template.body}
                  </span>
                  {template.buttons.length > 0 ? (
                    <span className="mt-1 block text-xs text-ink-muted">{template.buttons.join(' · ')}</span>
                  ) : null}
                  {template.reason ? <span className="mt-1 block text-xs text-danger">{template.reason}</span> : null}
                </span>
              </label>
            );
          })}
        </div>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * Each subject's mails in an A/B test (ADR-113): sent, opened, clicked — the
 * winner marked once chosen, or when it will be while the test waits.
 */
function AbTestResults({ campaign }: { campaign: CampaignDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const test = campaign.abTest!;
  return (
    <span className="mt-1 flex flex-col gap-0.5 text-xs text-ink-muted">
      {(['A', 'B'] as const).map((variant) => {
        const stats = test.variants[variant];
        return (
          <span key={variant} className={cx(test.winner === variant && 'font-medium text-success')}>
            {t('abTestLine', {
              variant,
              sent: count(stats.sent, locale),
              opened: share(stats.opened, stats.sent, locale),
              clicked: share(stats.clicked, stats.sent, locale),
            })}
            {test.winner === variant ? ` · ${t('abTestWinner')}` : ''}
          </span>
        );
      })}
      {campaign.status === 'TESTING' && test.testEndsAt ? <span>{t('abTestEndsAt', { when: dateTime(test.testEndsAt, locale) })}</span> : null}
    </span>
  );
}
